import { useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const METHOD_LABEL = { razorpay: "Razorpay", cash: "Cash", bank_transfer: "Bank transfer", upi: "UPI" };

// Shows refund state for a cancelled order (or fulfilled return) and offers the
// admin a Process Refund action when one is pending. kind: "order" | "return".
export default function AdminRefundControl({ kind = "order", target, onDone, compact = false }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("upi");
  const [reference, setReference] = useState("");
  const [refundDate, setRefundDate] = useState(() => new Date().toISOString().slice(0, 10));

  if (!target) return null;
  const status = target.refund_status;
  if (!status || status === "not_required") return null;

  const isOnline = target.payment_method === "online";
  const defaultAmount = target.refund_amount ?? target.total ?? 0;
  const endpoint = kind === "return" ? `/admin/returns/${target.id}/process-refund` : `/admin/orders/${target.id}/process-refund`;
  const tid = `${kind}-refund-${target.id}`;

  if (status === "processed" || status === "initiated") {
    return (
      <div data-testid={`${tid}-done`} className="text-[11px] text-green-700 leading-snug">
        <div className="font-semibold">Refunded ₹{Number(target.refund_amount || 0).toLocaleString("en-IN")}</div>
        <div className="text-gray-600">
          {METHOD_LABEL[target.refund_method] || target.refund_method || "-"}
          {target.refund_id ? ` · ${target.refund_id}` : target.refund_reference ? ` · ${target.refund_reference}` : ""}
        </div>
        <div className="text-gray-500">{target.refund_date ? new Date(target.refund_date).toLocaleString("en-IN") : ""}</div>
      </div>
    );
  }

  const process = async () => {
    setBusy(true);
    try {
      const payload = isOnline
        ? { amount: Number(amount) || defaultAmount }
        : { amount: Number(amount) || defaultAmount, method, reference: reference || null, refund_date: refundDate || null };
      const { data } = await api.post(endpoint, payload);
      if ((data.refund_status || "") === "failed") toast.error(`Refund failed: ${data.refund_error || "Razorpay error"}`);
      else toast.success("Refund recorded");
      setOpen(false);
      onDone && onDone(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not process refund");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        data-testid={`${tid}-pending`}
        onClick={() => { setAmount(String(defaultAmount || "")); setOpen(true); }}
        className={`inline-flex items-center rounded-full text-[11px] font-medium ${compact ? "px-2.5 py-1" : "px-3 py-1.5"} ${status === "failed" ? "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100" : "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"}`}
      >
        {status === "failed" ? "Refund failed — retry" : "Process Refund"}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white" data-testid={`${tid}-dialog`}>
          <DialogHeader>
            <DialogTitle>Process refund · #{(target.id || "").slice(0, 8).toUpperCase()}</DialogTitle>
          </DialogHeader>
          {isOnline ? (
            <div className="text-sm space-y-3">
              <p className="text-gray-600">This will refund the customer through Razorpay to their original payment method.</p>
              <div>
                <Label className="text-xs">Refund amount (₹)</Label>
                <Input data-testid={`${tid}-amount`} type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-9 mt-1" />
              </div>
              <Button data-testid={`${tid}-confirm`} onClick={process} disabled={busy} className="w-full rounded-full bg-[#F48E2E] hover:bg-[#D97815]">
                {busy ? "Processing…" : `Refund ₹${Number(amount || 0).toLocaleString("en-IN")} via Razorpay`}
              </Button>
            </div>
          ) : (
            <div className="text-sm space-y-3">
              <p className="text-gray-600">Record the offline refund you made for this {target.payment_method === "cod" ? "COD" : "bank transfer"} order.</p>
              <div>
                <Label className="text-xs">Refund amount (₹)</Label>
                <Input data-testid={`${tid}-amount`} type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-9 mt-1" />
              </div>
              <div>
                <Label className="text-xs">Refund method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid={`${tid}-method`} className="h-9 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input data-testid={`${tid}-date`} type="date" value={refundDate} onChange={(e) => setRefundDate(e.target.value)} className="h-9 mt-1" />
              </div>
              <div>
                <Label className="text-xs">Reference / UTR <span className="text-gray-400 font-normal">optional</span></Label>
                <Input data-testid={`${tid}-reference`} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR / transaction note" className="h-9 mt-1" />
              </div>
              <Button data-testid={`${tid}-confirm`} onClick={process} disabled={busy} className="w-full rounded-full bg-[#F48E2E] hover:bg-[#D97815]">
                {busy ? "Saving…" : "Mark refund as processed"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
